/* FAQs as the theme's own collapsible rows, closed by default.
 *
 * The FAQ section is a custom-liquid block: every question is an <h4> with the
 * answer in a <p> after it, all of it expanded, which reads nothing like the
 * "Product Overview" rows further up the page. Each pair is rebuilt here as a
 * clone of a real collapsible row, so the markup, the plus/minus icon and the
 * open/close behaviour are the theme's own rather than an imitation.
 */
(() => {
  /* One rule, enforced for the life of the page: a collapsible tab is open only
     because the shopper opened it.
     Three things push against that. The block settings mark "Product Overview"
     open, so the page arrives with a wall of manufacturer text unrolled;
     oshea-pdp.js re-opens it when it rebuilds the mobile column (at load, again
     ~1.2s later, and on every crossing of the 1024px breakpoint); and the rows
     it clones into that column arrive carrying whatever state they had.
     Stating the rule and holding it is steadier than trying to land a one-shot
     close between those, and it leaves templates/product.json (44 KB) and
     oshea-pdp.js (40 KB, minified) untouched. */
  const TAB = '.product__block--collapsible_tab';

  const close = (d) => {
    if (!d.open || d.dataset.opdUserOpened) return;
    d.open = false;
    d.removeAttribute('open');
    const summary = d.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  };

  const closeWithin = (node) => {
    if (!node || node.nodeType !== 1) return;
    if (node.matches(TAB)) node.querySelectorAll('details[open]').forEach(close);
    else node.querySelectorAll(TAB + ' details[open]').forEach(close);
  };

  /* A row the shopper opens stays open — marked before the theme's accordion
     script toggles it, so the observer below never fights a real tap. */
  const markIntent = (e) => {
    const summary = e.target.closest && e.target.closest('summary');
    const details = summary && summary.closest('details');
    if (details && details.closest(TAB)) details.dataset.opdUserOpened = '1';
  };
  document.addEventListener('click', markIntent, true);
  document.addEventListener('keydown', markIntent, true);

  closeWithin(document.documentElement);

  new MutationObserver((records) => {
    records.forEach((rec) => {
      if (rec.type === 'attributes') {
        if (rec.target.tagName === 'DETAILS' && rec.target.closest(TAB)) close(rec.target);
      } else {
        rec.addedNodes.forEach(closeWithin);
      }
    });
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['open'],
  });

  const start = () => {
    closeWithin(document.documentElement);

    const faqs = document.querySelector('.faqs');
    if (!faqs || faqs.dataset.opdFaq) return;

    /* Two shapes of FAQ data exist in the catalogue, and both render the same
       flat text, so the difference is invisible until you look for it. About a
       third of products wrap the pairs in the theme's own markup —
       <div class="one-column"><div class="my-accordion">… — and the rest store
       the bare <div><h4>…</h4><p>…</p></div> pairs with no wrapper at all.
       Looking only for .my-accordion therefore left the accordion missing on
       every product of the second kind, which is most of the catalogue.
       The host is simply whatever element the pairs are children of, so fall
       back to the first question's grandparent: h4 -> its pair div -> the host. */
    let host = faqs.querySelector('.my-accordion');
    if (!host) {
      const firstHeading = faqs.querySelector('h4');
      const pair = firstHeading && firstHeading.parentElement;
      if (pair && pair.parentElement && pair.parentElement !== faqs) host = pair.parentElement;
      else if (pair) host = faqs;
    }
    const rows = host ? [...host.children].filter((r) => r.querySelector('h4')) : [];
    if (!rows.length) return;

    /* A real row from the buy column, used as the template. Without one
       (a product with no collapsible tabs) the section is left alone —
       hand-written markup would drift from the theme's. */
    const template = document.querySelector('.product__block--collapsible_tab');
    if (!template || !template.querySelector('summary')) return;

    const list = document.createDocumentFragment();

    rows.forEach((row, i) => {
      const heading = row.querySelector('h4');
      const question = heading.textContent.trim();
      const answer = [...row.children]
        .filter((child) => child !== heading)
        .map((child) => child.outerHTML)
        .join('');

      const block = template.cloneNode(true);
      const details = block.querySelector('details');
      const summary = block.querySelector('summary');
      const label = summary.querySelector('.h5, p');
      const content = block.querySelector('.accordion-details__content');
      if (!details || !label || !content) return;

      /* ids are cloned too, and duplicates break both the theme's accordion
         script and the page's accessibility tree. */
      block.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      content.id = 'opd-faq-content-' + i;
      details.id = 'opd-faq-' + i;
      summary.setAttribute('aria-controls', content.id);

      /* Closed by default, unlike the row it was cloned from. */
      details.open = false;
      details.removeAttribute('open');
      details.setAttribute('aria-expanded', 'false');

      /* The inline font-size the block settings write on the label is kept, so
         a question is set in exactly the same type as "Product Overview". */
      label.textContent = question;
      content.innerHTML = answer;

      list.appendChild(block);
    });

    /* The whole set goes inside one more row of the same kind, so the FAQs read
       as a single "+" line like "Product Overview" rather than as five. The
       section's <h2> is moved into that row's summary instead of being
       rewritten as a <p>: the heading stays in the document outline for search
       engines, and it picks up the row's own type by taking its classes. */
    const group = template.cloneNode(true);
    const gDetails = group.querySelector('details');
    const gSummary = group.querySelector('summary');
    const gLabel = gSummary.querySelector('.h5, p');
    const gContent = group.querySelector('.accordion-details__content');
    if (!gDetails || !gLabel || !gContent) return;

    group.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    gContent.id = 'opd-faq-group-content';
    gDetails.id = 'opd-faq-group';
    gSummary.setAttribute('aria-controls', gContent.id);
    gDetails.open = false;
    gDetails.removeAttribute('open');
    gDetails.setAttribute('aria-expanded', 'false');

    const heading = faqs.querySelector('h2');
    if (heading) {
      heading.className = gLabel.className;
      heading.setAttribute('style', gLabel.getAttribute('style') || '');
      gLabel.replaceWith(heading);
    } else {
      gLabel.textContent = 'FAQs';
    }

    /* The theme's AccordionGroup closes every other open row when one opens,
       searching `this.closest('.accordion-parent') || document` — so without a
       scope, tapping a question closed the wrapper it sits inside. Marking the
       wrapper's content as an accordion-parent is the theme's own way to say
       "these five are a group of their own": a question now closes only its
       four siblings, and the wrapper stays open. */
    gContent.classList.add('accordion-parent');
    gContent.replaceChildren(list);
    group.classList.add('opd-faq__group');

    /* When the fallback above resolved the host to .faqs itself, replacing its
       children would take the <h2> with it — and the <h2> has just been moved
       into the group's summary, so only the pair rows should go. */
    if (host === faqs) rows.forEach((r) => r.remove());
    else host.replaceChildren(group);
    if (host === faqs) faqs.appendChild(group);
    faqs.classList.add('opd-faq');
    faqs.dataset.opdFaq = '1';
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
